import axios from 'axios';
import {
  Booking,
  CustomResponse,
  Schedule,
  ScheduleCreate,
  ScheduleGet,
  ScheduleUpdate
} from '../models';

const BASE_URL = process.env.REACT_APP_BASE_URL_LOCAL || 'https://api.fullimpact.training/v1';


export async function getStatus() {
  return axios({
    url: `${BASE_URL}/status`,
    method: 'get',
  })
    .then(({ data }) => data)
    .catch(() => ({
      version: 'unknown'
    }));
}

/**
 * ================================================
 *   BOOKING HELPERS
 * ================================================
*/

export function getBooking(id: string, token?: string): Promise<CustomResponse> {
  return axios({
    url: `${BASE_URL}/schedule/booking/${id}`,
    method: 'get',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(({ data }) => data)
    .catch(error => {
      return {
        success: false,
        message: error.message
      };
    });
}

export function claimBooking(id: string, booking: Booking, token?: string): Promise<CustomResponse> {
  return axios({
    url: `${BASE_URL}/schedule/booking/${id}`,
    method: 'post',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: booking
  })
    .then(({ data }) => data)
    .catch(error => {
      return {
        success: false,
        message: error.message
      };
    });
}

export function deleteBooking(id: string, token?: string): Promise<CustomResponse> {
  return axios({
    url: `${BASE_URL}/schedule/booking/${id}`,
    method: 'delete',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(({ data }) => data)
    .catch(error => {
      return {
        success: false,
        message: error.message
      };
    });
}


/**
 * ================================================
 *   SCHEDULE HELPERS
 * ================================================
*/

export async function createSchedule(schedule: ScheduleCreate, token?: string): Promise<CustomResponse> {
  return await axios({
    url: `${BASE_URL}/schedule/create`,
    method: 'post',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: schedule
  }).then(({ data }) => ({
    success: data.success,
    message: 'Successfully created new Schedule',
    count: data.results.length
  }))
    .catch(error => ({
      success: false,
      message: `Unable to create schedule (${error.message})`
    }));
}

export async function updateSchedule(id: string, schedule: ScheduleUpdate, token?: string): Promise<CustomResponse> {
  return await axios({
    url: `${BASE_URL}/schedule/${id}`,
    method: 'put',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: schedule
  }).then(({ data }) => data)
    .catch(error => ({
      success: false,
      message: `Unable to update schedule (${error.message})`
    }));
}

export async function updateRecurrence(id: string, schedule: ScheduleUpdate, token?: string): Promise<CustomResponse> {
  return await axios({
    url: `${BASE_URL}/schedule/recurrence/${id}`,
    method: 'put',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: schedule
  }).then(({ data }) => data)
    .catch(error => ({
      success: false,
      message: `Unable to update schedule (${error.message})`
    }));
}

export async function getSchedule(id: string): Promise<ScheduleGet> {
  return await axios({
    url: `${BASE_URL}/schedule/${id}`,
    method: 'get',
  })
    .then(({ data }) => data)
    .catch(() => ({
      success: false
    }));
}

export async function getFilteredSchedules(start: Date, end: Date, token?: string): Promise<Array<Schedule>> {
  const result = await axios({
    url: `${BASE_URL}/schedule`,
    method: 'post',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: {
      start,
      end
    }
  })
    .then(({ data }) => data)
    .catch(() => ({
      success: false
    }));

  return result.success ? result.data : [];
}

export async function deleteSchedule(id: string, token?: string): Promise<CustomResponse> {
  return await axios({
    url: `${BASE_URL}/schedule/${id}`,
    method: 'delete',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(({ data }) => data)
    .catch(() => ({
      success: false,
      message: 'Unable to Delete Schedule'
    }));
}

export async function deleteSchedules(recurrenceId: string, token?: string): Promise<CustomResponse> {
  return await axios({
    url: `${BASE_URL}/schedule/group/${recurrenceId}`,
    method: 'delete',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(({ data }) => data)
    .catch(() => ({
      success: false
    }));
}

export async function cleanSchedules(end: Date, token?: string): Promise<CustomResponse> {
  const result = await axios({
    url: `${BASE_URL}/schedule`,
    method: 'post',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    data: {
      end
    }
  })
    .then(({ data }) => data)
    .catch(() => ({
      success: false
    }));

  return result.success ? result.data : [];
}